/**
 * AnalyticsChart.tsx
 *
 * Shared chart component used by all 4 analytics metric charts.
 * Models are driven entirely by `selectedModels` from the API response.
 *
 * Time framing: the chart owns its own view controls. It receives the FULL
 * measured series and windows it for display, anchored on TODAY, with preset
 * lenses (7D / 30D / 90D) plus a custom date range. It looks backward (measured
 * history) and forward (the dashed projection roadmap) in one blended view.
 */
import { useState, useRef, useEffect, useId } from "react";
import {
  ComposedChart, Line, Area, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import {
  TrendingUp, TrendingDown, LineChartIcon,
  BarChart3, AreaChartIcon, X, Calendar,
  Zap, RefreshCw, FileText, ChevronDown, Check,
} from "lucide-react";
import { Card }                from "../ui/card";
import { getModelConfig }      from "../../constants/chart-model-config";
import { type PerformanceEvent } from "../../data/performance-events";
import {
  SEGMENT_KEY,
  type SeriesIncompleteRun,
  type SeriesRebaselineMark,
} from "@/app/services/analytics/deep-signal-series-api";
import { IncompleteRunNotice } from "@/app/components/shared/incomplete-run-notice";
import { RebaselineNotice } from "@/app/components/shared/rebaseline-notice";

// Rebaseline marker colour (Recharts SVG props cannot read CSS vars).
const REBASELINE_HEX = "#7E5FE0";

/** A point's series segment; 0 when the point carries none (every series that
 *  was never rebaselined, and every non-Deep-Signal series). */
function segmentOf(point: any): number {
  const s = Number(point?.[SEGMENT_KEY]);
  return Number.isFinite(s) ? s : 0;
}

const DAY_MS = 86_400_000;

// Three event kinds, each with its own colour + icon, so the timeline reads at a
// glance. The raw event.type ("launch"/"scan"/"content"/"optimization") is
// mapped onto these without adding any new data field.
export type EventKind = "optimization" | "rerun" | "content";
const KIND_STYLE: Record<EventKind, { color: string; Icon: any; label: string }> = {
  optimization: { color: "#7E5FE0", Icon: Zap,       label: "Optimizations" },     // wave deploy / optimization shipped
  rerun:        { color: "#FF6D66", Icon: RefreshCw, label: "Analysis re-runs" },  // measurement re-run
  content:      { color: "#28A9D2", Icon: FileText,  label: "Content published" },
};
function eventKindOf(type: string): EventKind {
  if (type === "scan") return "rerun";
  if (type === "content") return "content";
  return "optimization"; // launch + optimization
}
function eventStyleFor(type: string) {
  return KIND_STYLE[eventKindOf(type)];
}

// Marker drawn at the top of an event's reference line: a circular badge with
// the type icon. Reports hover up so the annotation card can render at the x.
function EventMarker({
  viewBox,
  event,
  onHover,
}: {
  viewBox?: { x?: number; y?: number };
  event: PerformanceEvent;
  onHover: (h: { event: PerformanceEvent; x: number } | null) => void;
}) {
  const x = viewBox?.x ?? 0;
  const y = viewBox?.y ?? 0;
  const { color, Icon } = eventStyleFor(event.type);
  return (
    <g
      transform={`translate(${x}, ${y})`}
      style={{ cursor: "pointer" }}
      onMouseEnter={() => onHover({ event, x })}
      onMouseLeave={() => onHover(null)}
    >
      <circle cx={0} cy={3} r={11} fill="var(--card)" stroke={color} strokeWidth={1.5} />
      <Icon x={-5.5} y={-2.5} width={11} height={11} color={color} />
    </g>
  );
}

export type DataView  = "absolute" | "percent-change" | "normalized";
export type ChartType = "line" | "area" | "bar";
export type RangePreset = "all" | "7d" | "30d" | "90d" | "custom";

export interface ChartDataPoint {
  date:      string;
  timestamp: number;
  [modelKey: string]: number | string;
}

export interface AnalyticsChartProps {
  timeSeriesData:   ChartDataPoint[];
  selectedModels:   string[];
  isLoading:        boolean;
  error:            Error | null;

  metricTitle:        React.ReactNode;
  metricName:         string;
  metricUnit?:        string;
  /**
   * Hide the whole headline block (title + big value + change badge), e.g. when
   * the parent renders its own persistent header above the chart so it does not
   * shift between views. The range controls on the right stay.
   */
  hideHeadline?:      boolean;
  // Custom content for the headline slot (e.g. the Variables lens places its
  // title + Chart/Ranked toggle here instead of a meaningless aggregate value).
  headerContent?:     React.ReactNode;
  metricTermProps?: {
    definition:      string;
    calculation:     string;
    interpretation:  string;
    goodRange:       string;
  };

  insightContext?: string;

  brandName?:              string;
  onNavigateToInsights?:   () => void;

  // Opt-in longitudinal extras. Passing `events` (even an empty array) enables
  // the time axis, the projection roadmap, and the events control.
  events?:          PerformanceEvent[];
  projectionDate?:  string | null;

  // True when the series is synthesized from a single run rather than measured
  // snapshots; renders a small "Illustrative" label so it never reads as trend.
  isIllustrative?:  boolean;

  // Plotted runs flagged incomplete (excluded + failed calls over the
  // threshold). Listed under the chart with the count by lane; nothing renders
  // when the list is empty, so a clean series looks exactly as before.
  incompleteRuns?:  SeriesIncompleteRun[];

  // Series rebaselines between plotted reads. Each one breaks every line at
  // its marker (no line is drawn across a rebaseline as if nothing changed),
  // draws the marker, and is listed under the chart with its change list.
  // Absent or empty: one unbroken series, exactly as before.
  rebaselineMarks?: SeriesRebaselineMark[];
}

export function AnalyticsChart({
  timeSeriesData,
  selectedModels,
  isLoading,
  error,
  metricTitle,
  metricUnit = "%",
  hideHeadline,
  headerContent,
  events,
  projectionDate,
  isIllustrative,
  incompleteRuns,
  rebaselineMarks,
}: AnalyticsChartProps) {

  const eventsEnabled = events !== undefined;
  const [dataView,    setDataView]    = useState<DataView>("absolute");
  const [chartType,   setChartType]   = useState<ChartType>("line");
  // Default to the full measured history so the baseline + every re-measurement
  // are visible out of the box. A fixed trailing window (e.g. 90d-from-today)
  // silently drops a baseline measured longer ago than the window — which is the
  // common case for migrated brands — leaving a flat single-point chart even
  // when later runs exist. "All" anchors the left edge on the earliest reading.
  const [rangePreset, setRangePreset] = useState<RangePreset>("all");
  const [customStart, setCustomStart] = useState<string>("");
  const [customEnd,   setCustomEnd]   = useState<string>("");
  // Pan: drag or scroll shifts the visible time window left/right. Reset on
  // preset change. spanRef/dragRef let the native listeners read fresh values.
  const [panMs, setPanMs] = useState(0);
  const chartBoxRef = useRef<HTMLDivElement>(null);
  const spanRef = useRef(0);
  const dragRef = useRef<{ x: number; pan: number; span: number; w: number } | null>(null);
  const [eventsOn,    setEventsOn]    = useState(true);
  const [eventTypes,  setEventTypes]  = useState<Record<EventKind, boolean>>({
    optimization: true, rerun: true, content: true,
  });
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);
  const [hoveredEvent, setHoveredEvent] = useState<{ event: PerformanceEvent; x: number } | null>(null);
  const [allModels,   setAllModels]   = useState<string[]>([]);
  const [visibleModels, setVisibleModels] = useState<Set<string>>(new Set());
  const typeMenuRef = useRef<HTMLDivElement>(null);
  const [showViewHelp, setShowViewHelp] = useState(false);
  const rawUid = useId();
  const uid = rawUid.replace(/[:]/g, "");
  // Area-fill gradient id. Series keys can contain spaces/punctuation (e.g. a
  // research-variable label like "Pricing transparency"); an SVG id with a space
  // is invalid, so the gradient url fails to resolve and the area fills BLACK.
  // Sanitize the key to keep the id valid and the same in def + reference.
  const gradId = (k: string) => `grad-${uid}-${k.replace(/[^a-zA-Z0-9_-]/g, "_")}`;

  useEffect(() => {
    if (selectedModels.length === 0) return;
    setAllModels(prev => {
      const custom = prev.filter(m => !selectedModels.includes(m));
      return [...selectedModels, ...custom];
    });
    setVisibleModels(prev => {
      const custom = [...prev].filter(m => !selectedModels.includes(m));
      return new Set([...selectedModels, ...custom]);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedModels.join(",")]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (typeMenuRef.current && !typeMenuRef.current.contains(e.target as Node))
        setTypeMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reset the pan offset whenever the range selection changes.
  useEffect(() => { setPanMs(0); }, [rangePreset, customStart, customEnd]);

  // Drag-to-pull pans the window. Listeners on window so the drag survives the
  // cursor leaving the chart box.
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const d = dragRef.current;
      if (!d) return;
      const dxPx = e.clientX - d.x;
      setPanMs(d.pan - dxPx * (d.span / d.w)); // drag right -> view moves earlier
    };
    const onUp = () => { if (dragRef.current) { dragRef.current = null; document.body.style.cursor = ""; } };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => { window.removeEventListener("mousemove", onMove); window.removeEventListener("mouseup", onUp); };
  }, []);

  const getCfg = (key: string) => getModelConfig(key, allModels);

  const removeModel = (key: string) => {
    if (selectedModels.includes(key)) return;
    setAllModels(p => p.filter(k => k !== key));
    setVisibleModels(p => { const s = new Set(p); s.delete(key); return s; });
  };

  const toggleModel = (key: string) => {
    setVisibleModels(p => { const s = new Set(p); s.has(key) ? s.delete(key) : s.add(key); return s; });
  };

  if (isLoading) {
    return (
      <Card className="p-4 md:p-6 flex items-center justify-center" style={{ minHeight: "300px" }}>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full animate-pulse" style={{ backgroundColor: "var(--alpha-teal)" }} />
          <span style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)" }}>Loading…</span>
        </div>
      </Card>
    );
  }

  if (error || !timeSeriesData.length) {
    return (
      <Card className="p-4 md:p-6 flex items-center justify-center" style={{ minHeight: "200px" }}>
        <span style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)", textAlign: "center", overflowWrap: "anywhere" }}>
          {error?.message ?? "No data available."}
        </span>
      </Card>
    );
  }

  // ── Time window (look-back + short forward margin) ──────────────────────────
  // The presets show the last N days of measured history, plus a short forward
  // margin for the projection roadmap:
  //   All -> earliest reading -> today + projection margin (full history; the
  //          default, so a baseline measured before any trailing window still
  //          shows alongside the latest re-measurement).
  //   7D  -> the trailing 7 days (today on the right edge).
  //   30D -> the trailing 30 days + a 2-week projection margin.
  //   90D -> the trailing 90 days + a 2-week projection margin.
  // (Earlier these were today-CENTRED — 45 back / 45 forward for 90D — which hid
  // any measured point older than ~half the window, e.g. a baseline measured
  // weeks before the recent re-measurements, and left the forward half empty.)
  // Where there is no reading before the window start, the line simply begins at
  // the first reading we have. Nothing is fabricated.
  const todayTs = Date.now();
  // How far past today the presets extend, so the dashed projection still shows
  // without dominating the chart the way a 45-day forward half did.
  const PROJECTION_MARGIN_MS = 14 * DAY_MS;
  // The forward margin exists to make room for forward elements: future scan
  // events or a scheduled next measurement. When nothing is scheduled past
  // today, reserving two dead weeks just pads the right side of All/30D with
  // empty space — so the margin collapses to a single day of breathing room
  // and the Today line sits at the right edge.
  const hasForwardElement =
    (events ?? []).some(e => { const t = Date.parse(e.date); return !Number.isNaN(t) && t > todayTs; }) ||
    (projectionDate ? Date.parse(projectionDate) > todayTs : false);
  const FORWARD_MARGIN_MS = hasForwardElement ? PROJECTION_MARGIN_MS : DAY_MS;

  // Distinct, ascending timestamps. The backend serialises same-day runs with a
  // "#n" suffix that parses to an identical epoch; nudge collisions forward so
  // the time axis renders each point instead of stacking them on one x.
  const sortedSeries = (timeSeriesData as any[])
    .map(p => ({ ...p, timestamp: Number((p as any).timestamp) }))
    .filter(p => !Number.isNaN(p.timestamp))
    .sort((a, b) => a.timestamp - b.timestamp);
  let _prevTs = -Infinity;
  const cleanSeries = sortedSeries.map(p => {
    const t = p.timestamp <= _prevTs ? _prevTs + 60 * 60 * 1000 : p.timestamp;
    _prevTs = t;
    return { ...p, timestamp: t };
  });

  const customStartTs = customStart ? Date.parse(customStart) : NaN;
  const customEndTs   = customEnd ? Date.parse(`${customEnd}T23:59:59`) : NaN;

  // 7D stays a tight trailing view (today on the right edge). 30D/90D look back
  // the full N days and add a short forward margin for the projection so older
  // measured points (e.g. a baseline measured weeks earlier) stay on-screen.
  let winStartTs: number;
  let winEndTs: number;
  if (rangePreset === "all") {
    // Full measured history: anchor the left edge on the earliest reading so the
    // baseline always shows, however long ago it was measured. Keeps the short
    // forward margin for the projection roadmap on the right.
    winStartTs = cleanSeries.length ? cleanSeries[0].timestamp : todayTs - 90 * DAY_MS;
    winEndTs   = todayTs + FORWARD_MARGIN_MS;
  } else if (rangePreset === "custom") {
    winStartTs = Number.isNaN(customStartTs) ? (cleanSeries.length ? cleanSeries[0].timestamp : todayTs) : customStartTs;
    winEndTs   = Number.isNaN(customEndTs) ? todayTs : customEndTs;
  } else if (rangePreset === "7d") {
    winStartTs = todayTs - 7 * DAY_MS;
    winEndTs   = todayTs;
  } else {
    const spanDays = rangePreset === "90d" ? 90 : 30;
    winStartTs = todayTs - spanDays * DAY_MS;
    winEndTs   = todayTs + FORWARD_MARGIN_MS;
  }

  // Measured readings inside the window (the left side of the chart).
  const baseSeries = cleanSeries.filter(p => p.timestamp >= winStartTs && p.timestamp <= winEndTs);

  // A trailing preset (7D/30D/90D) can legitimately contain NO readings when the
  // measurement cadence is sparse (e.g. a weekly/monthly run and a 7-day window).
  // In that case we must NOT render a fabricated 0.0% — that reads as the metric
  // collapsing to zero. Instead we show the latest KNOWN reading in the header
  // and an explicit empty-range message in place of the chart.
  const windowEmpty = baseSeries.length === 0;
  const latestKnownPoint = cleanSeries.length ? cleanSeries[cleanSeries.length - 1] : null;

  // Whether a trailing preset actually contains readings. Used to DISABLE empty
  // presets so a sparse cadence (weeks between runs) can't produce a blank chart.
  const presetHasData = (preset: RangePreset): boolean => {
    if (preset === "all" || preset === "custom") return true;
    let s: number;
    let e: number;
    if (preset === "7d") { s = todayTs - 7 * DAY_MS; e = todayTs; }
    else { const d = preset === "90d" ? 90 : 30; s = todayTs - d * DAY_MS; e = todayTs + FORWARD_MARGIN_MS; }
    return cleanSeries.some(p => p.timestamp >= s && p.timestamp <= e);
  };

  const data: ChartDataPoint[] = (() => {
    if (!baseSeries.length) return [];
    if (dataView === "absolute") return baseSeries;
    return baseSeries.map(point => {
      // % Change and Normalized rebase on the first point in view OF THE SAME
      // SERIES SEGMENT: after a rebaseline the new baseline is the reference,
      // never a read measured on a different battery.
      const ref = baseSeries.find(p => segmentOf(p) === segmentOf(point)) ?? baseSeries[0];
      const result: any = { ...point };
      allModels.forEach(key => {
        const b = (ref as any)[key] ?? 0;
        const p = (point as any)[key] ?? 0;
        result[key] = dataView === "percent-change"
          ? (b > 0 ? ((p - b) / b) * 100 : 0)
          : (b > 0 ? (p / b) * 100 : 100);
      });
      return result;
    });
  })();

  const currentData  = data[data.length - 1];
  // "vs last period" anchors to the immediately prior plotted read, not a fixed
  // window-back offset. With a sparse series (a handful of runs) an 8-point-back
  // anchor collapsed to the window's first point, so a per-run delta was reported
  // against the series start (e.g. "+X vs April") instead of the previous run.
  const previousData = data[Math.max(0, data.length - 2)];
  // The latest read and the one before it sit on different sides of a series
  // rebaseline: the latest is a NEW BASELINE, and a "vs last period" delta
  // would compare reads taken on different batteries. Shown as such instead.
  const crossesRebaseline = data.length > 1 && segmentOf(currentData) !== segmentOf(previousData);

  const primaryKey  = selectedModels[0] ?? allModels[0] ?? "overall";
  const currentVal  = windowEmpty
    ? Number((latestKnownPoint as any)?.overall ?? (latestKnownPoint as any)?.[primaryKey] ?? 0)
    : ((currentData as any)?.overall ?? (currentData as any)?.[primaryKey] ?? 0);
  const previousVal = (previousData as any)?.overall ?? (previousData as any)?.[primaryKey] ?? 0;
  // Report the change in the metric's own units (percentage POINTS for the rate
  // metrics, index points for the AI Index) rather than a relative percent. A
  // relative % on top of an already-percentage metric is misleading when the
  // base is tiny — e.g. 1.2% → 7.2% reads as "+500%" but is really +6.0 points.
  const change      = currentVal - previousVal;

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;
    const title = typeof label === "number"
      ? new Date(label).toLocaleDateString("en-US", { month: "short", day: "numeric" })
      : label;
    const rows = payload.filter((e: any) =>
      e?.value != null && !String(e?.dataKey ?? "").endsWith("__proj"));
    if (rows.length === 0) return null;
    return (
      <div className="rounded-lg shadow-lg p-3 md:p-4 border" style={{ backgroundColor: "var(--background)", borderColor: "var(--border)", maxWidth: "240px" }}>
        <p style={{ fontSize: "var(--text-sm)", fontWeight: "var(--font-weight-semibold)", marginBottom: "8px" }}>{title}</p>
        {rows.map((e: any, i: number) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: e.color, flexShrink: 0 }} />
            <span style={{ fontSize: "var(--text-sm)", color: "var(--foreground)", overflowWrap: "anywhere" }}>
              {e.name}: {e.value.toFixed(1)}{dataView === "absolute" ? metricUnit : "%"}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // yDomain is computed after the visible window is known (see absYDomain below).
  const visible      = allModels.filter(k => visibleModels.has(k));

  // Model colours resolve to data-viz hexes (Recharts SVG props can't read vars).
  const MODEL_HEX: Record<string, string> = {
    openai: "#12B76A", chatgpt: "#12B76A",
    perplexity: "#FF6D66", claude: "#28A9D2", gemini: "#7E5FE0",
  };
  // Literal fallback palette for non-model series (research variables, personas,
  // competitors). getModelConfig returns CSS var() strings, which do NOT resolve
  // as SVG paint attributes — an undefined one (e.g. --solar-orange) renders as
  // black dots with no line. Assign distinct hexes by series position instead.
  const EXTRA_HEX = ["#F59E0B", "#EC4899", "#8B5CF6", "#14B8A6", "#EF4444", "#0EA5E9", "#A855F7", "#F97316"];
  const colorFor = (key: string) => {
    const known = MODEL_HEX[key.toLowerCase()];
    if (known) return known;
    const idx = allModels.indexOf(key);
    return EXTRA_HEX[(idx < 0 ? 0 : idx) % EXTRA_HEX.length];
  };

  // A dot on EVERY measured point (slightly larger on the latest), so the real
  // measurements are visible and the line reads as "points connected" rather
  // than a continuous curve. This makes bursts — e.g. several re-measurements
  // within a day — legible as distinct readings instead of an unexplained cliff.
  // Projection rows carry no value for `key`, so the renderer is a no-op there.
  //
  // SERIES REBASELINES: a gap row (every series null) is inserted between two
  // consecutive reads on different segments, at the marker. Lines and areas
  // do not connect across a null, so each segment draws as its own line.
  const renderData: any[] = (() => {
    if (!data.length || !data.some((p, i) => i > 0 && segmentOf(p) !== segmentOf(data[i - 1]))) {
      return data;
    }
    const rows: any[] = [];
    data.forEach((point, i) => {
      if (i > 0 && segmentOf(point) !== segmentOf(data[i - 1])) {
        const prevTs = Number((data[i - 1] as any).timestamp);
        const nextTs = Number((point as any).timestamp);
        const mark = (rebaselineMarks ?? []).find(m => m.afterTimestamp === nextTs || (m.timestamp > prevTs && m.timestamp < nextTs));
        const gapTs = mark && mark.timestamp > prevTs && mark.timestamp < nextTs ? mark.timestamp : (prevTs + nextTs) / 2;
        const gapLabel = mark && !mark.rebaselines.length && mark.batteryChanged ? "Questions changed" : "Rebaselined";
        const gap: any = { date: `${gapLabel}${" ".repeat(i)}`, timestamp: gapTs, __gap: true };
        allModels.forEach(k => { gap[k] = null; });
        gap.overall = null;
        rows.push(gap);
      }
      rows.push(point);
    });
    return rows;
  })();
  const lastIdx = renderData.length - 1;
  // Sparse series (a handful of real runs) print the latest value above its
  // dot so the current reading is legible without hovering.
  const sparseValueLabels = data.length <= 8;
  const measuredDot = (color: string) => (props: any) => {
    const { cx, cy, index, value } = props;
    if (value == null || cx == null || cy == null) return <g key={`d-${index}`} />;
    const isLast = index === lastIdx;
    return (
      <g key={`d-${index}`}>
        <circle cx={cx} cy={cy} r={isLast ? 4 : 3}
          fill={color} stroke="var(--card)" strokeWidth={1.5} />
        {sparseValueLabels && isLast && (
          <text x={cx} y={cy - 10} fontSize={12} fontWeight={700} fill={color} textAnchor="middle">
            {Math.round(Number(value) * 10) / 10}
          </text>
        )}
      </g>
    );
  };

  const lineProps = (key: string) => ({
    type: "monotone" as const, dataKey: key,
    stroke: colorFor(key), strokeWidth: 2.5, strokeLinecap: "round" as const,
    dot: measuredDot(colorFor(key)), activeDot: { r: 6, strokeWidth: 0 },
  });

  // Shared segmented-control styling (range / view / chart-type toggles).
  const segTrack: React.CSSProperties = {
    display: "inline-flex", gap: 3, padding: 3,
    background: "var(--muted)", borderRadius: 9,
  };
  const segBtn = (on: boolean): React.CSSProperties => ({
    border: "none", cursor: "pointer", borderRadius: 7, padding: "5px 11px",
    fontSize: 13, fontWeight: on ? 600 : 500,
    background: on ? "var(--card)" : "transparent",
    color: on ? "var(--foreground)" : "var(--muted-foreground)",
    boxShadow: on ? "0 1px 2px rgba(13,13,15,.12)" : "none",
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6,
  });
  const dateInput: React.CSSProperties = {
    height: 30, borderRadius: 7, border: "1px solid var(--border)",
    background: "var(--card)", color: "var(--foreground)", fontSize: 12,
    padding: "0 8px", outline: "none",
  };

  // ── Projection (forward roadmap) ────────────────────────────────────────────
  const lastActualTs = data.length ? Number((data[data.length - 1] as any).timestamp) : NaN;

  // Horizon caps the projection: in custom mode it stops at the custom end date.
  const projHorizon = rangePreset === "custom" && !Number.isNaN(customEndTs) ? customEndTs : Infinity;

  const futureScanTs = eventsEnabled
    ? (events ?? [])
        .filter((e) => e.type === "scan")
        .map((e) => Date.parse(e.date))
        .filter((t) => !Number.isNaN(t) && t > lastActualTs && t <= projHorizon)
        .sort((a, b) => a - b)
    : [];

  // A "Next measurement" marker is only drawn from a REAL scheduled date
  // (projectionDate). Without a schedule there is no fallback date: fabricating
  // one from the past cadence claims a measurement nobody scheduled.
  const scheduledTs = projectionDate ? Date.parse(projectionDate) : NaN;
  const flatProjTs = Number.isNaN(lastActualTs)
    ? NaN
    : (!Number.isNaN(scheduledTs) && scheduledTs > lastActualTs && scheduledTs <= projHorizon)
      ? scheduledTs
      : NaN;

  const projTsList = futureScanTs.length
    ? futureScanTs
    : (!Number.isNaN(flatProjTs) && flatProjTs > lastActualTs ? [flatProjTs] : []);

  // Forward roadmap rides the Events master toggle ("Events" off = measured only).
  const hasProjection = projTsList.length > 0 && !Number.isNaN(lastActualTs);
  // The illustrative ramp exists ONLY for single-measurement brands (its
  // original preview job). With two or more real measurements the chart shows
  // measured data plus the next-measurement marker, never invented values.
  const rampAllowed   = data.length <= 1;
  const projectionOn  = hasProjection && eventsOn && rampAllowed;

  // Longitudinal mode = real time axis + today/projection framing.
  const longitudinalMode = eventsEnabled || hasProjection;
  const isBaseline       = longitudinalMode && data.length <= 1;

  const chartData: any[] = (() => {
    if (!projectionOn) return renderData;
    const rows = renderData.map(d => ({ ...d } as any));
    const last = rows[rows.length - 1];
    visible.forEach(k => { last[`${k}__proj`] = (last as any)[k]; });
    // Dashed forward roadmap holds FLAT at the last measured value. It used to
    // apply the illustrative PROJECTION_RAMP multipliers, but a rising invented
    // curve on a client-facing page reads as a prediction of improvement nobody
    // measured. The dashed line's job is "measurements continue on this
    // schedule", not "numbers will go up".
    projTsList.forEach((ts) => {
      const mult = 1;
      const projPoint: any = { date: "Projected", timestamp: ts };
      visible.forEach(k => {
        const base = Number((last as any)[k]) || 0;
        const projected = Math.round(base * mult * 10) / 10;
        // Only the Absolute (0-100) view caps at 100. Normalized and % Change
        // views legitimately exceed 100, so clamping there flattens the whole
        // projection onto 100 (the bug that made the dashed line dive).
        projPoint[`${k}__proj`] = dataView === "absolute" ? Math.min(100, projected) : projected;
      });
      rows.push(projPoint);
    });
    // Extend the held projection out to the window's right edge so the forward
    // half of a today-centred window is filled rather than trailing off into
    // empty space. Holds the last projected value; the axis clips the overflow.
    const lastRow: any = rows[rows.length - 1];
    const lastProjTs = Number(lastRow?.timestamp);
    if (Number.isFinite(winEndTs) && Number.isFinite(lastProjTs) && winEndTs > lastProjTs) {
      const fill: any = { date: "Projected", timestamp: winEndTs };
      visible.forEach(k => { fill[`${k}__proj`] = lastRow[`${k}__proj`]; });
      rows.push(fill);
    }
    return rows;
  })();

  const formatTick = (ts: number) => new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });

  // Visible domain. Default (presets): begin where the data begins, so there is
  // no blank gap to the left of the first reading. We clamp the left edge up to
  // the first reading inside the window; as you switch 7/30/90 the scale simply
  // changes. Custom keeps the literal start so a manual range CAN show blank
  // space before the data. allowDataOverflow on the axis clips the projection
  // that runs past the right edge so the dashed line still reaches it.
  const effStartTs = (rangePreset === "custom" || !baseSeries.length)
    ? winStartTs
    : baseSeries[0].timestamp;
  const pad = Math.max((winEndTs - effStartTs) * 0.03, DAY_MS);
  const baseDomainMin = effStartTs - pad;
  const baseDomainMax = winEndTs + pad;
  // Apply the pan offset to get the actually-visible window.
  spanRef.current = baseDomainMax - baseDomainMin;
  const domainMin = baseDomainMin + panMs;
  const domainMax = baseDomainMax + panMs;

  // Y scales to the MEASURED data inside the window only. Projected values are
  // deliberately excluded: letting the synthetic ramp stretch the axis is what
  // flattened real 2-4 point movements into unreadable lines.
  const yWin: number[] = [];
  chartData.forEach(d => {
    const t = Number((d as any).timestamp);
    if (Number.isNaN(t) || t < domainMin || t > domainMax) return;
    visible.forEach(k => {
      // null is a rebaseline gap row, not a reading (Number(null) is 0).
      const raw = (d as any)[k];
      if (raw == null) return;
      const v = Number(raw);                           if (!Number.isNaN(v)) yWin.push(v);
    });
  });
  const yLo = yWin.length ? Math.floor(Math.min(...yWin)) : 0;
  const yHi = yWin.length ? Math.ceil(Math.max(...yWin)) : 100;
  const yGap = Math.max((yHi - yLo) * 0.12, 2);
  // Integer-aligned domain + explicit evenly spaced ticks. Fractional domain
  // bounds let Recharts emit uneven tick values (61 / 66 / 71 / 80), which reads
  // as a broken ruler; aligning the top of the domain to a whole number of steps
  // keeps every gap identical.
  const absLo = Math.max(0, Math.floor(yLo - yGap));
  const absStep = Math.max(1, Math.ceil((Math.min(100, yHi + yGap) - absLo) / 4));
  const absHi = Math.min(100, absLo + absStep * 4);
  const absYDomain: [number, number] = [absLo, absHi];
  const absTicks = [0, 1, 2, 3, 4].map(i => absLo + absStep * i).filter(v => v <= absHi);
  // Normalized values are rebased to 100 and can grow well past 110 (e.g. +20%),
  // so the axis must scale to the data instead of a fixed 90–110 window that
  // clips high-growth lines.
  const normYDomain: [number, number] = [Math.max(0, yLo - yGap), yHi + yGap];

  // Caption: the explicit selected window.
  const capFmt = (ts: number) => new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const rangeCaption = `${capFmt(effStartTs)} to ${capFmt(winEndTs)}`;

  const yDomain: any =
    dataView === "normalized"       ? normYDomain
    : dataView === "percent-change" ? ["auto", "auto"]
    : absYDomain;

  // Events in the visible window, by kind (for filter counts + markers).
  const eventsInWindow = eventsEnabled
    ? (events ?? [])
        .map(e => ({ e, t: Date.parse(e.date), kind: eventKindOf(e.type) }))
        .filter(({ t }) => !Number.isNaN(t) && t >= domainMin && t <= domainMax)
    : [];
  const kindCounts: Record<EventKind, number> = { optimization: 0, rerun: 0, content: 0 };
  eventsInWindow.forEach(({ kind }) => { kindCounts[kind] += 1; });

  const xAxisEl = longitudinalMode ? (
    <XAxis dataKey="timestamp" type="number" domain={[domainMin, domainMax]} tickFormatter={formatTick}
      axisLine={false} tickLine={false} tickMargin={10} allowDataOverflow
      tick={{ fill: "var(--muted-foreground)", fontSize: 12, fontWeight: 500 }} />
  ) : (
    <XAxis dataKey="date" axisLine={false} tickLine={false} tickMargin={10}
      tick={{ fill: "var(--muted-foreground)", fontSize: 12, fontWeight: 500 }} />
  );

  const projectionLines = projectionOn
    ? visible.map(k => (
        <Line key={`${k}__proj`} type="monotone" dataKey={`${k}__proj`} stroke={colorFor(k)}
          strokeWidth={2.5} strokeLinecap="round" strokeDasharray="5 5" dot={false} activeDot={false as any} connectNulls legendType="none" />
      ))
    : null;

  const nextMeasureTs = !Number.isNaN(flatProjTs) ? flatProjTs : (futureScanTs.length ? futureScanTs[0] : NaN);
  const projectionMarker = (eventsOn && Number.isFinite(nextMeasureTs) && (projectionOn ? futureScanTs.length === 0 : true)) ? (
    <ReferenceLine x={nextMeasureTs} stroke="var(--muted-foreground)" strokeDasharray="2 4" strokeOpacity={0.6}
      label={{ value: `Next measurement: ${formatTick(nextMeasureTs)}`, position: "insideBottomRight", fontSize: 10, fill: "var(--muted-foreground)" } as any} />
  ) : null;

  // "Today" marker: always on in longitudinal mode (today is real, not projected).
  const todayMarker = longitudinalMode ? (
    <ReferenceLine x={todayTs} stroke="var(--muted-foreground)" strokeDasharray="2 3" strokeOpacity={0.8}
      label={{ value: "Today", position: "insideTopLeft", fontSize: 10, fill: "var(--muted-foreground)" } as any} />
  ) : null;

  // Event markers: master toggle + per-type filter, clipped to the window.
  const eventRefLines = (eventsEnabled && eventsOn)
    ? eventsInWindow
        .filter(({ kind }) => eventTypes[kind])
        .map(({ e, t }) => (
          <ReferenceLine key={e.id} x={t} stroke={eventStyleFor(e.type).color} strokeDasharray="4 4" strokeOpacity={0.6}
            label={(props: any) => <EventMarker viewBox={props.viewBox} event={e} onHover={setHoveredEvent} />} />
        ))
    : null;

  // Series rebaseline markers: always on (they are part of the measurement
  // record, not an optional event layer). One per crossing in the plotted
  // window, placed where the gap row breaks the lines.
  const marksInView = (rebaselineMarks ?? []).filter(m =>
    data.some(p => Number((p as any).timestamp) === m.afterTimestamp),
  );
  const rebaselineRefLines = marksInView.map(m => {
    const versions = m.rebaselines.map(r => `v${r.battery_version}`).join(", ");
    const markLabel = m.rebaselines.length ? `Rebaselined · ${versions}` : "Questions changed · not comparable";
    const gap = renderData.find(r => r.__gap && Number(r.timestamp) > m.beforeTimestamp && Number(r.timestamp) < m.afterTimestamp);
    const x = longitudinalMode ? (gap ? Number(gap.timestamp) : m.timestamp) : gap?.date;
    if (x === undefined) return null;
    return (
      <ReferenceLine key={`rebaseline-${m.afterTimestamp}`} x={x} stroke={REBASELINE_HEX} strokeWidth={1.5} strokeOpacity={0.85}
        label={{ value: markLabel, position: "insideTopRight", fontSize: 10, fontWeight: 600, fill: REBASELINE_HEX } as any} />
    );
  });

  const eventsBorder = eventsOn ? "var(--signal-blue-20, rgba(40,169,210,.28))" : "var(--border)";

  return (
    <Card className="p-4 md:p-6">
      <div className="space-y-5 md:space-y-6">

        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          {headerContent ? (
            <div className="min-w-0">{headerContent}</div>
          ) : hideHeadline ? (
            <div />
          ) : (
          <div className="min-w-0">
            <h3
              style={{
                fontSize: "clamp(var(--text-lg), 3.5vw, var(--text-xl))",
                fontWeight: "var(--font-weight-semibold)",
                marginBottom: "4px",
                overflowWrap: "anywhere",
                wordBreak: "break-word",
              }}
            >
              {metricTitle}
            </h3>
            <div className="flex items-baseline gap-2 flex-wrap">
              <span
                style={{
                  fontSize: 36,
                  fontWeight: 800,
                  letterSpacing: "-.02em",
                  fontVariantNumeric: "tabular-nums",
                  color: "var(--foreground)",
                }}
              >
                {currentVal.toFixed(1)}{metricUnit}
              </span>
              {windowEmpty ? (
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--muted-foreground)", backgroundColor: "var(--muted)", border: "1px solid var(--border)", borderRadius: 9999, padding: "2px 10px" }}>
                  Latest reading · no data in range
                </span>
              ) : isBaseline ? (
                <span style={{ fontSize: 12, fontWeight: 600, color: "var(--signal-blue)", backgroundColor: "var(--muted)", border: "1px solid var(--border)", borderRadius: 9999, padding: "2px 10px" }}>
                  Baseline
                </span>
              ) : crossesRebaseline ? (
                <span
                  title="The latest read is the series' new baseline after a planned rebaseline. A change from the previous read would compare reads measured differently."
                  style={{ fontSize: 12, fontWeight: 600, color: REBASELINE_HEX, backgroundColor: "var(--spectral-violet-5)", border: "1px solid var(--spectral-violet-30)", borderRadius: 9999, padding: "2px 10px" }}
                >
                  New baseline · not comparable with the previous read
                </span>
              ) : (
                <span className="inline-flex items-center" style={{ gap: 4, fontSize: 14, fontWeight: 600, color: change >= 0 ? "#04938F" : "var(--infrared-coral)" }}>
                  {change >= 0
                    ? <TrendingUp style={{ width: 15, height: 15 }} />
                    : <TrendingDown style={{ width: 15, height: 15 }} />}
                  {change >= 0 ? "+" : ""}{change.toFixed(1)} pts vs last period
                </span>
              )}
              {isIllustrative && (
                <span
                  title="Derived from your latest run, not measured snapshots. It becomes a measured trend after your next run."
                  style={{ fontSize: 12, fontWeight: 600, color: "var(--muted-foreground)", backgroundColor: "var(--muted)", border: "1px dashed var(--border)", borderRadius: 9999, padding: "2px 10px" }}
                >
                  Illustrative
                </span>
              )}
            </div>
          </div>
          )}

          {/* Range lenses + custom date inputs */}
          <div className="w-full sm:w-auto sm:text-right">
            <div style={segTrack}>
              {/* Only show presets that actually contain readings (plus All and
                  Custom). A trailing window with no data at this cadence isn't a
                  disabled button, it's simply not offered — so the control adapts
                  to the data instead of cluttering with dead options. */}
              {/* 90D pulled for now: measurement cadence doesn't have the
                  granularity to make it read differently from All yet. */}
              {(["all", "7d", "30d", "custom"] as RangePreset[]).filter(presetHasData).map(r => (
                <button key={r} onClick={() => setRangePreset(r)} style={segBtn(rangePreset === r)}>
                  {r === "custom" ? "Custom" : r === "all" ? "All" : r.toUpperCase()}
                </button>
              ))}
            </div>
            {rangePreset === "custom" && (
              <div className="flex items-center sm:justify-end" style={{ gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                <input type="date" value={customStart} max={customEnd || undefined}
                  onChange={e => setCustomStart(e.target.value)} style={dateInput} aria-label="Start date" />
                <span style={{ fontSize: 12, color: "var(--muted-foreground)" }}>to</span>
                <input type="date" value={customEnd} min={customStart || undefined}
                  onChange={e => setCustomEnd(e.target.value)} style={dateInput} aria-label="End date" />
              </div>
            )}
            <div style={{ fontSize: 13, color: "var(--muted-foreground)", marginTop: 6 }}>
              {rangeCaption}
            </div>
          </div>
        </div>

        {/* ── View + events + chart-type controls ──────────────────────── */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex flex-col" style={{ gap: 5 }}>
            <div className="flex items-center" style={{ gap: 8 }}>
              <span style={{ fontSize: 13, color: "var(--muted-foreground)" }} className="hidden sm:inline">View</span>
              <div style={segTrack}>
                {(["absolute", "percent-change", "normalized"] as DataView[]).map(v => (
                  <button key={v} onClick={() => setDataView(v)} style={segBtn(dataView === v)}>
                    {v === "absolute" ? "Absolute" : v === "percent-change" ? "% Change" : "Normalized"}
                  </button>
                ))}
              </div>
            </div>
            <button
              onClick={() => setShowViewHelp(s => !s)}
              style={{ alignSelf: "flex-start", background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 500, color: "var(--spectral-violet)", padding: 0 }}
            >
              What do these views mean?
            </button>
          </div>

          <div className="flex items-center" style={{ gap: 8 }}>
            {/* Events split control: master toggle + per-type filter menu.
                Hidden until the events feed actually has entries — an empty
                toggle invites a click that changes nothing. */}
            {eventsEnabled && (events?.length ?? 0) > 0 && (
              <div className="relative" ref={typeMenuRef}>
                <div
                  className="inline-flex items-center"
                  style={{
                    height: 32, borderRadius: 8, overflow: "hidden",
                    border: `1px solid ${eventsBorder}`,
                    background: eventsOn ? "var(--signal-blue-10, rgba(40,169,210,.1))" : "var(--card)",
                    color: eventsOn ? "#28A9D2" : "var(--muted-foreground)",
                  }}
                >
                  <button
                    onClick={() => setEventsOn(v => !v)}
                    className="inline-flex items-center"
                    style={{ gap: 6, height: "100%", padding: "0 10px 0 12px", background: "transparent", border: "none", color: "inherit", cursor: "pointer", fontSize: 12, fontWeight: 600 }}
                  >
                    <Calendar style={{ width: 14, height: 14 }} /> Events
                  </button>
                  <button
                    onClick={() => setTypeMenuOpen(o => !o)}
                    aria-label="Filter event types"
                    style={{ width: 26, height: "100%", borderLeft: `1px solid ${eventsBorder}`, background: "transparent", color: "inherit", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  >
                    <ChevronDown style={{ width: 13, height: 13 }} />
                  </button>
                </div>
                {typeMenuOpen && (
                  <div
                    className="absolute z-50"
                    style={{ top: "calc(100% + 8px)", right: 0, width: 212, background: "var(--card)", border: "1px solid var(--border)", borderRadius: 10, boxShadow: "0 14px 34px -14px rgba(13,13,15,.3)", padding: 6 }}
                  >
                    <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".04em", color: "var(--muted-foreground)", padding: "7px 10px 5px" }}>
                      Show event types
                    </div>
                    {(Object.keys(KIND_STYLE) as EventKind[]).map(kind => {
                      const ks = KIND_STYLE[kind];
                      const KIcon = ks.Icon;
                      const on = eventTypes[kind];
                      return (
                        <button
                          key={kind}
                          onClick={() => setEventTypes(p => ({ ...p, [kind]: !p[kind] }))}
                          className="w-full inline-flex items-center"
                          style={{ gap: 10, padding: "8px 10px", borderRadius: 7, background: "transparent", border: "none", cursor: "pointer", textAlign: "left" }}
                          onMouseEnter={e => (e.currentTarget.style.background = "var(--muted)")}
                          onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                        >
                          <span
                            style={{
                              width: 16, height: 16, borderRadius: 5, flexShrink: 0,
                              display: "inline-flex", alignItems: "center", justifyContent: "center",
                              border: `1.5px solid ${on ? ks.color : "var(--silver-mist, #B2B5B9)"}`,
                              background: on ? ks.color : "transparent", color: "#fff",
                            }}
                          >
                            {on && <Check style={{ width: 11, height: 11 }} />}
                          </span>
                          <KIcon style={{ width: 13, height: 13, color: ks.color, flexShrink: 0 }} />
                          <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: "var(--foreground)" }}>{ks.label}</span>
                          <span style={{ fontSize: 12, color: "var(--muted-foreground)", fontVariantNumeric: "tabular-nums" }}>{kindCounts[kind]}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
            {eventsEnabled && (events?.length ?? 0) > 0 && (
              <div style={{ width: 1, height: 24, background: "var(--border)" }} className="hidden sm:block" />
            )}
            <div style={segTrack}>
              {[
                { t: "line" as ChartType, I: LineChartIcon },
                { t: "area" as ChartType, I: AreaChartIcon },
                { t: "bar"  as ChartType, I: BarChart3 },
              ].map(({ t, I }) => (
                <button key={t} onClick={() => setChartType(t)} style={{ ...segBtn(chartType === t), padding: "4px 9px" }}>
                  <I style={{ width: 16, height: 16 }} />
                </button>
              ))}
            </div>
          </div>
        </div>
        {showViewHelp && (
          <div style={{ fontSize: 12, lineHeight: 1.5, color: "var(--muted-foreground)", background: "var(--muted)", border: "1px solid var(--border)", borderRadius: 8, padding: "10px 12px" }}>
            <span style={{ color: "var(--foreground)", fontWeight: 600 }}>Absolute</span> shows the raw score.{" "}
            <span style={{ color: "var(--foreground)", fontWeight: 600 }}>% Change</span> shows movement since the first point in view.{" "}
            <span style={{ color: "var(--foreground)", fontWeight: 600 }}>Normalized</span> rebases every model to 100 at the start so trajectories compare regardless of starting level.
          </div>
        )}

        {/* ── Chart ─────────────────────────────────────────────────────── */}
        <div
          ref={chartBoxRef}
          onMouseDown={(e) => {
            dragRef.current = { x: e.clientX, pan: panMs, span: spanRef.current, w: chartBoxRef.current?.clientWidth || 800 };
            document.body.style.cursor = "grabbing";
          }}
          style={{ width: "100%", height: "clamp(260px, 40vw, 380px)", position: "relative", cursor: "grab", touchAction: "pan-y", userSelect: "none" }}
        >
          {windowEmpty && (
            <div className="flex items-center justify-center" style={{ position: "absolute", inset: 0, padding: "0 24px" }}>
              <div className="text-center" style={{ maxWidth: 340 }}>
                <p style={{ fontSize: 14, fontWeight: 600, color: "var(--foreground)", marginBottom: 4 }}>
                  No measurements in this range
                </p>
                <p style={{ fontSize: 13, color: "var(--muted-foreground)", lineHeight: 1.5 }}>
                  The nearest reading is outside {rangeCaption}. Switch to "All" to see the full history.
                </p>
              </div>
            </div>
          )}
          {!windowEmpty && eventsEnabled && eventsOn && hoveredEvent && (() => {
            const es = eventStyleFor(hoveredEvent.event.type);
            const EvIcon = es.Icon;
            return (
              <div
                style={{
                  position: "absolute", left: hoveredEvent.x, top: 0, transform: "translateX(-50%)",
                  zIndex: 10, width: 224, maxWidth: "80%", pointerEvents: "none",
                  backgroundColor: "var(--card)", border: "1px solid var(--border)", borderRadius: 10,
                  boxShadow: "0 14px 34px -14px rgba(13,13,15,.3)", padding: "13px 15px",
                }}
              >
                <div className="flex items-center" style={{ gap: 7, marginBottom: 5 }}>
                  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 20, height: 20, borderRadius: 6, background: "var(--muted)", color: es.color, flexShrink: 0 }}>
                    <EvIcon style={{ width: 12, height: 12 }} />
                  </span>
                  <p style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>{hoveredEvent.event.title}</p>
                </div>
                <p style={{ fontSize: 12, color: "var(--muted-foreground)", lineHeight: 1.5, marginBottom: 6 }}>
                  {hoveredEvent.event.description}
                </p>
                <p style={{ fontSize: 11, color: "var(--silver-mist, #888780)", fontWeight: 500 }}>
                  {new Date(hoveredEvent.event.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                </p>
              </div>
            );
          })()}
          {!windowEmpty && (
          <ResponsiveContainer width="100%" height="100%">
            {/* One ComposedChart for all three types so the forward projection
                line overlays consistently on lines, areas AND bars. Recharts does
                not render a <Line> placed inside <AreaChart>/<BarChart>, which is
                why area/bar previously stopped dead at the last reading. */}
            <ComposedChart data={chartData} margin={{ top: 24, right: 18, left: 0, bottom: 10 }}>
              {chartType === "area" && (
                <defs>
                  {visible.map(k => (
                    <linearGradient key={k} id={gradId(k)} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={colorFor(k)} stopOpacity={0.18} />
                      <stop offset="100%" stopColor={colorFor(k)} stopOpacity={0} />
                    </linearGradient>
                  ))}
                </defs>
              )}
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
              {xAxisEl}
              <YAxis domain={yDomain} ticks={dataView === "absolute" ? absTicks : undefined} allowDataOverflow axisLine={false} tickLine={false} tick={{ fill: "var(--muted-foreground)", fontSize: 11 }} width={40} />
              <Tooltip content={<CustomTooltip />} cursor={chartType === "bar" ? { fill: "var(--muted)", opacity: 0.4 } : { stroke: "var(--border)", strokeWidth: 1 }} />
              {chartType === "line" && visible.map(k => <Line key={k} {...lineProps(k)} name={getCfg(k).name} />)}
              {chartType === "area" && visible.map(k => <Area key={k} {...lineProps(k)} name={getCfg(k).name} fill={`url(#${gradId(k)})`} fillOpacity={1} />)}
              {chartType === "bar" && visible.map(k => <Bar key={k} dataKey={k} name={getCfg(k).name} fill={colorFor(k)} opacity={0.9} radius={[3, 3, 0, 0]} />)}
              {projectionLines}
              {projectionMarker}
              {todayMarker}
              {eventRefLines}
              {rebaselineRefLines}
            </ComposedChart>
          </ResponsiveContainer>
          )}
        </div>

        {/* ── Series rebaselines in view: date, what changed, the note ──── */}
        {marksInView.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {marksInView.flatMap(m => m.rebaselines.map((r, i) => (
              <RebaselineNotice
                key={`${m.afterTimestamp}-${r.id}`}
                rebaseline={r}
                newBaseline={i === m.rebaselines.length - 1}
                title={`Series rebaselined · battery v${r.battery_version} · new baseline ${m.afterDate}`}
                compact
              />
            )))}
          </div>
        )}

        {/* ── Incomplete runs among the plotted points ───────────────────── */}
        {(() => {
          const plotted = new Set(timeSeriesData.map(p => p.date));
          const marks = (incompleteRuns ?? []).filter(r => plotted.has(r.date));
          if (!marks.length) return null;
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {marks.map(r => (
                <div key={`${r.date}-${r.timestamp}`}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "var(--muted-foreground)" }}>
                    {r.date} run
                  </div>
                  <IncompleteRunNotice completeness={r.completeness} />
                </div>
              ))}
            </div>
          );
        })()}

        {/* ── Legend + Add Model ─────────────────────────────────────────── */}
        {/* Two legend layouts. The pill row was built for a handful of short
            model names; feed it seven long claim labels and it wraps into a
            ragged chip cloud. So when entries are many or long, the legend
            switches to an aligned table: dot + name (truncating, full text on
            hover) left, score and delta right-aligned in fixed columns, sorted
            by score. Click still toggles the series either way. */}
        {(() => {
          const legendStats = (key: string) => {
            // When the window has no readings, show each series' latest KNOWN
            // value (not a fabricated 0.0) and drop the meaningless delta.
            const val = windowEmpty
              ? Number((latestKnownPoint as any)?.[key] ?? (latestKnownPoint as any)?.overall ?? 0)
              : ((currentData as any)?.[key] ?? 0);
            const prev = (previousData as any)?.[key] ?? 0;
            // Across a rebaseline there is no like-for-like previous value.
            return { val, diff: crossesRebaseline ? NaN : val - prev };
          };
          const denseLegend = allModels.length > 5 || allModels.some(k => getCfg(k).name.length > 26);
          const legendKeys = denseLegend
            ? [...allModels].sort((a, b) => legendStats(b).val - legendStats(a).val)
            : allModels;
          return (
            <div style={{ borderTop: "1px solid var(--border)", paddingTop: "10px" }}>
              <div className={denseLegend ? undefined : "flex flex-wrap gap-2 items-center"}
                style={denseLegend ? { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(360px, 1fr))", columnGap: 28, rowGap: 2 } : undefined}>
                {legendKeys.map(key => {
                  const cfg  = getCfg(key);
                  const c    = colorFor(key);
                  const { val, diff } = legendStats(key);
                  const isBackend = selectedModels.includes(key);
                  const on = visibleModels.has(key);
                  if (denseLegend) {
                    return (
                      <button
                        key={key}
                        onClick={() => toggleModel(key)}
                        title={cfg.name}
                        style={{
                          display: "grid", gridTemplateColumns: "10px minmax(0,1fr) auto auto",
                          alignItems: "center", gap: 8, padding: "5px 6px", borderRadius: 6,
                          background: "none", border: "none", cursor: "pointer",
                          opacity: on ? 1 : 0.4, textAlign: "left",
                        }}
                      >
                        <span style={{ width: 10, height: 10, borderRadius: 9999, background: c }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--foreground)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {cfg.name}
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)", fontVariantNumeric: "tabular-nums", minWidth: 52, textAlign: "right" }}>
                          {val.toFixed(1)}{metricUnit}
                        </span>
                        {!windowEmpty && Number.isFinite(diff) ? (
                          <span style={{ fontSize: 12, color: diff >= 0 ? "#04938F" : "var(--infrared-coral)", fontVariantNumeric: "tabular-nums", minWidth: 58, textAlign: "right" }}>
                            {diff >= 0 ? "▲" : "▼"}{Math.abs(diff).toFixed(1)} pts
                          </span>
                        ) : <span />}
                      </button>
                    );
                  }
                  return (
                    <div key={key} className="flex items-center gap-1">
                      <button
                        onClick={() => toggleModel(key)}
                        className="inline-flex items-center"
                        style={{
                          gap: 7, height: 32, padding: "0 12px", borderRadius: 9,
                          background: "var(--muted)", border: "1px solid var(--border)",
                          opacity: on ? 1 : 0.45, cursor: "pointer",
                        }}
                      >
                        <span style={{ width: 10, height: 10, borderRadius: 9999, background: c, flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color: "var(--foreground)" }}>{cfg.name}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "var(--foreground)" }}>{val.toFixed(1)}{metricUnit}</span>
                        {!windowEmpty && Number.isFinite(diff) && (
                          <span style={{ fontSize: 12, color: diff >= 0 ? "#04938F" : "var(--infrared-coral)" }}>
                            {diff >= 0 ? "▲" : "▼"}{Math.abs(diff).toFixed(1)} pts
                          </span>
                        )}
                        {!isBackend && (
                          <span style={{ fontSize: 11, color: "var(--muted-foreground)", marginLeft: 2 }}>(custom)</span>
                        )}
                      </button>
                      {!isBackend && (
                        <button
                          onClick={() => removeModel(key)}
                          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px", color: "var(--muted-foreground)" }}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

      </div>
    </Card>
  );
}
